<template>
  <div>
    <el-container class="screen screen__products" direction="vertical">
      <product-info-marquee />
      <product-category-selector />
      <product-sub-category-selector
        :categories="childCategories"
        :activeSubcategory="activeSubcategory"
      />
      <product-sub-category
        v-for="(subCategory, index) in childCategories"
        :category="subCategory"
        :key="index"
      />
      <product-sales-training-cta />
    </el-container>
    <footer-main />
  </div>
</template>

<script>
import products from '@/components/products';
import ProductCategorySelector from '@/components/products/ProductCategorySelector.vue';
import ProductSubCategory from '@/components/products/ProductSubCategory.vue';
import ProductSubCategorySelector from '@/components/products/ProductSubCategorySelector.vue';
import ProductSalesTrainingCta from '@/components/products/ProductSalesTrainingCta.vue';
import ProductInfoMarquee from '@/components/products/ProductInfoMarquee.vue';
import FooterMain from '@/components/footer/FooterMain.vue';
import i18n from '@/configured/vue-i18n';
import { mapGetters } from 'vuex';
import {
  scrollToProduct,
  upToSecondHash,
} from '../components/products/product.util';

export default {
  name: 'Products',
  props: ['category', 'label'],
  data() {
    return {
      activeSubcategory: null, // The hash/slug of the active subcategory
      containerEl: null,
    };
  },
  computed: {
    categoryObject() {
      return this.getCategoryById(this.categoryNumber);
    },
    ...mapGetters(products.store.namespace, [
      'getCategoryById',
      'hasMenuProductsInCategory',
      'getFilteredCategories',
    ]),
    selectedCategory() {
      if (!this.getFilteredCategories) {
        return [];
      }
      return this.getFilteredCategories.find(
        (category) => category.slug === this.$route.params.category
      );
    },
    childCategories() {
      if (this.selectedCategory) {
        return this.selectedCategory.children;
      }
      return [];
    },
    categoryIds() {
      return this.childCategories.map((category) => category.slug);
    },
  },
  components: {
    ProductCategorySelector,
    ProductInfoMarquee,
    ProductSalesTrainingCta,
    ProductSubCategory,
    ProductSubCategorySelector,
    FooterMain,
  },
  methods: {
    setPageTitle,
    upToSecondHash,
    onScroll,
  },
  watch: {
    '$route.path'() {
      this.$nextTick(this.onScroll);
    },
  },
  beforeMount() {
    this.setPageTitle();
  },
  updated() {
    this.setPageTitle();
  },
  mounted() {
    this.containerEl = document.getElementById('app-main');
    if (this.containerEl) {
      this.containerEl.addEventListener('scroll', this.onScroll, {
        passive: true,
      });
      this.onScroll();
    }

    /* Handle scrolling to hash on navigation with hash */
    // Wait until DOM is rendered, then navigate to any IDs that are in the route hash
    // Navigates to relevant parts of the page when navigating to hash value from a different route
    const scrollToHash = () => {
      const hash = this.$route.hash;
      if (!hash) return;
      const slug = hash.slice(1);
      scrollToProduct(slug);
    };

    this.$nextTick(scrollToHash);
  },

  beforeDestroy() {
    if (this.containerEl) {
      this.containerEl.removeEventListener('scroll', this.onScroll);
    }
  },
};

//////////

function setPageTitle() {
  const translationKey = `pageTitleProducts`;
  const pageTitle = i18n.i18n.t(translationKey);
  const divider = i18n.i18n.t(`pageTitleDivider`);
  const siteName = i18n.i18n.t('siteName');
  if (this.categoryObject && this.categoryObject.title) {
    document.title = this.categoryObject.title + divider + siteName;
  } else if (pageTitle === translationKey) {
    document.title = siteName;
  } else {
    document.title = pageTitle + divider + siteName;
  }
}

// Updates the hash in the URL and the activeSubcategory (informs nav button styling) to equal the currently scrolled-to subcategory slug
function onScroll() {
  const container = this.containerEl;
  const containerRect = container.getBoundingClientRect();
  const midpoint = containerRect.top + containerRect.height / 2;

  const visible = this.categoryIds
    .map((slug) => {
      const el = document.getElementById(slug);
      if (!el) return null;

      const rect = el.getBoundingClientRect();

      const isVisible =
        rect.bottom > containerRect.top && rect.top < containerRect.bottom;

      return isVisible ? { slug, rect } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.rect.top - b.rect.top);

  if (visible.length === 0) return;

  const topOne = visible[0];
  const topTwo = visible[1] || null;

  // First visible element crosses midpoint?
  if (topOne.rect.bottom >= midpoint) {
    this.activeSubcategory = topOne.slug;
  } else if (topTwo && topTwo.rect.top <= midpoint) {
    this.activeSubcategory = topTwo.slug;
  }

  const { origin, hash } = window.location;
  if (!hash.includes(this.activeSubcategory)) {
    const fullUrl =
      origin + this.upToSecondHash(hash) + '#' + this.activeSubcategory;
    history.replaceState(null, '', fullUrl);
  }
}
</script>

<style lang="scss" scoped>
@import 'element-theme-chalk/src/mixins/mixins.scss';

.page {
  &--container {
    margin: 0 !important;
  }
}
</style>
